/**
 * Garde d'accès aux routes /admin/*. Vérifie que l'utilisateur connecté
 * a au moins un des rôles autorisés. Le RBAC backend reste l'autorité —
 * ce guard évite d'afficher une UI inutile et signale la restriction.
 *
 * Convention de rôles alignée sur `lib/role-permissions.ts` :
 *   cure / vicaire / secretaire / secretaire_general / econome
 *
 * Variantes acceptées : minuscules (api-side) ET majuscules (legacy mobile).
 */
import { useLocation } from "wouter";
import { ShieldAlert, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/lib/i18n/i18n";

interface Props {
  /** Liste des rôles autorisés. Par défaut : tous les rôles "staff paroissial". */
  allowedRoles?: string[];
  fallbackTitle?: string;
  fallbackMessage?: string;
  children: React.ReactNode;
}

const DEFAULT_ALLOWED = [
  "cure", "vicaire", "secretaire", "secretaire_general", "econome",
  // Variantes
  "CURE", "VICAIRE", "SECRETAIRE", "SECRETAIRE_GENERAL", "ECONOME",
  "admin", "ADMIN", "PARISH_ADMIN",
];

export default function AdminGuard({
  allowedRoles = DEFAULT_ALLOWED,
  fallbackTitle,
  fallbackMessage,
  children,
}: Props) {
  const t = useTranslation();
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const title = fallbackTitle ?? t("common.guardTitle");
  const message = fallbackMessage ?? t("common.guardMessage");

  const role = (user?.role ?? "").toLowerCase();
  const allowedLower = allowedRoles.map((r) => r.toLowerCase());
  const isAllowed = !!user && allowedLower.includes(role);

  if (isAllowed) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-muted-bg p-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
          <ShieldAlert size={28} />
        </div>
        <h2 className="text-xl font-bold text-brand-primary mb-2" data-testid="admin-guard-title">{title}</h2>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <p className="text-xs text-gray-400 mb-6">
          {t("common.currentRole")} : <span className="font-mono">{user?.role ?? t("common.notConnected")}</span>
        </p>
        <button
          onClick={() => setLocation("/dashboard")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-white bg-[var(--brand-navy)]"
          data-testid="admin-guard-back"
        >
          <ArrowLeft size={16}  aria-hidden="true" /> {t("common.backToDashboard")}
        </button>
      </div>
    </div>
  );
}
